import { AlertTriangle, CheckCircle2, SearchX } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { CompanyOutletStrip } from './CompanyOutletStrip';
import { SettingsDirectoryGroup } from './SettingsDirectoryGroup';
import { SettingsFindBar } from './SettingsFindBar';
import { needsAttention } from './summaries';
import { useSettingsDirectory } from './useSettingsDirectory';

const normalize = (value: string) => value.toLowerCase().trim();

/**
 * Settings landing page: a directory of every settings area the user can
 * open, each row carrying a live one-line summary and a status tag only
 * when something needs a decision.
 */
export const SettingsDashboardPage: React.FC = () => {
  const { groups, rows, outlet } = useSettingsDirectory();
  const [query, setQuery] = useState('');
  const [onlyAttention, setOnlyAttention] = useState(false);

  const allRows = groups.flatMap((group) => group.rows);
  const attentionCount = allRows.filter((row) =>
    needsAttention(rows[row.id]?.summary?.status),
  ).length;
  const isChecking = allRows.some((row) => rows[row.id]?.isLoading);
  const showOnlyAttention = onlyAttention && attentionCount > 0;
  const placeName = outlet?.name ?? 'your company';

  const visibleGroups = useMemo(() => {
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    return groups
      .map((group) => ({
        ...group,
        rows: group.rows.filter((row) => {
          const state = rows[row.id];
          if (showOnlyAttention && !needsAttention(state?.summary?.status)) {
            return false;
          }
          if (terms.length === 0) return true;
          const haystack = normalize(
            [
              group.title,
              row.label,
              state?.summary?.text ?? '',
              ...row.keywords,
            ].join(' '),
          );
          return terms.every((term) => haystack.includes(term));
        }),
      }))
      .filter((group) => group.rows.length > 0);
  }, [groups, rows, query, showOnlyAttention]);

  const renderAttentionLine = () => {
    if (attentionCount > 0) {
      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="flex items-center gap-2 font-medium text-gray-900">
            <AlertTriangle
              size={16}
              className="shrink-0 text-amber-600"
              aria-hidden
            />
            <span className="tabular-nums">
              {attentionCount}{' '}
              {attentionCount === 1 ? 'thing needs' : 'things need'} attention
              at {placeName}
            </span>
          </p>
          <button
            type="button"
            aria-pressed={showOnlyAttention}
            onClick={() => setOnlyAttention((value) => !value)}
            className="rounded-md px-1.5 py-0.5 text-sm font-medium text-pos-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pos-primary"
          >
            {showOnlyAttention ? 'Show all settings' : 'Show only these'}
          </button>
        </div>
      );
    }
    if (isChecking) {
      return <p className="text-gray-500">Checking setup…</p>;
    }
    return (
      <p className="flex items-center gap-2 text-gray-700">
        <CheckCircle2
          size={16}
          className="shrink-0 text-pos-primary"
          aria-hidden
        />
        Everything at {placeName} looks set up
      </p>
    );
  };

  return (
    <div className="mx-auto max-w-5xl selection:bg-green-100 selection:text-green-950">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 [text-wrap:balance]">
          {outlet ? `Setup for ${outlet.name}` : 'Company setup'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Every setting you can manage, with anything that needs a decision
          flagged.
        </p>
      </header>

      <div className="mt-6">
        <SettingsFindBar value={query} onChange={setQuery} />
      </div>

      <div className="mt-4 min-h-[1.75rem] text-sm" aria-live="polite">
        {renderAttentionLine()}
      </div>

      <div className="mt-6 space-y-8 pb-4">
        {visibleGroups.map((group) => (
          <SettingsDirectoryGroup key={group.id} group={group} rows={rows} />
        ))}

        {visibleGroups.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center">
            <SearchX size={22} className="text-gray-300" aria-hidden />
            <p className="mt-3 font-medium text-gray-900">
              {groups.length === 0
                ? 'No settings are available to your account'
                : `No settings match “${query.trim()}”`}
            </p>
            {groups.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setOnlyAttention(false);
                }}
                className="mt-2 rounded-md px-1.5 py-0.5 text-sm font-medium text-pos-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pos-primary"
              >
                Clear search
              </button>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                Ask an owner to grant access to the areas you manage.
              </p>
            )}
          </div>
        ) : null}

        {!query && !showOnlyAttention ? <CompanyOutletStrip /> : null}
      </div>
    </div>
  );
};

export default SettingsDashboardPage;
